import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { Observable, Subject, Subscription, take, takeUntil } from "rxjs";
import { select_MOUI_ApplicationDetails, select_MOUI_DynamicTableColumn } from "../state-management/mobi-office-ui.selector";
import { GET_MOBIOFFICE_UI_DynamicTableColumn } from "../state-management/mobi-office-ui.action";
import { StorageHandlerService } from "./storage-handler.service";
import { UnSubscriber } from "../UnSubscriber";
import { FileType } from "../models";

@Injectable({
  providedIn: "root",
})
export class GlobalService extends UnSubscriber {
  private readonly store = inject(Store);
  private readonly storageHandlerService = inject(StorageHandlerService);

  constructor() {
    super();
  }

  readonly errorMessage: string = "Please fill all required fields";

  //#region Validation

  validateEmail(email: string) {
    const re = /^\S+@\S+$/;
    return re.test(email?.toString()?.trim());
  }

  validatePhoneNumber(phoneNumber: string) {
    const re = /^(\+\d{1,4})?\d{10}$/;
    return re.test(phoneNumber?.toString()?.trim());
  }

  validatePassword(password: string) {
    const re = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/;
    return re.test(password?.toString()?.trim());
  }

  validateUrl(url: string) {
    const re = new RegExp(/^(ftp|http|https):\/\/[^ "]+$/);
    return re.test(url?.toString()?.trim());
  }

  //#endregion

  //#region Global Function

  randomValue(isNumber: boolean, isString: boolean, length: number): string {
    const numbers: string = "0123456789";
    const alphabets: string = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz"; // Including lowercase letters
    let s = "";

    // Combine numbers and alphabets based on the parameters
    if (isNumber && isString) s = `${numbers}${alphabets}`;
    else if (isString) s = alphabets;
    else s = numbers;

    // Generate the random string of the desired length
    return Array.from({ length: length }, () => s.charAt(Math.floor(Math.random() * s.length))).join("");
  }

  indexSetter(_list: any[], keyName: string) {
    _list.map((x: any, i: number) => {
      x[keyName] = i + 1;
      return x;
    });
  }

  refreshComponent(_Router: Router) {
    _Router.routeReuseStrategy.shouldReuseRoute = () => false;
    _Router.onSameUrlNavigation = "reload";
    _Router.navigate([_Router.url]);
  }

  convertBase64ToBlob(base64: string) {
    const parts = base64.split(";base64,");
    const fileType = parts[0].split(":")[1];
    const decodedData = window.atob(parts[1]);
    const uInt8Array = new Uint8Array(decodedData.length);
    for (let i = 0; i < decodedData.length; ++i) {
      uInt8Array[i] = decodedData.charCodeAt(i);
    }
    const blob = new Blob([uInt8Array], { type: fileType });
    return window.URL.createObjectURL(blob);
  }

  getFileType(file: File): string {
    const fileType: string = file.name;
    let selectedFileType = "";

    if (this.imageRegex.test(fileType)) {
      selectedFileType = FileType.IMAGE;
    }

    if (this.pdfRegex.test(fileType)) {
      selectedFileType = FileType.PDF;
    }

    if (this.zipRegex.test(fileType)) {
      selectedFileType = FileType.ZIP;
    }

    if (this.rarRegex.test(fileType)) {
      selectedFileType = FileType.RAR;
    }

    if (this.gzRegex.test(fileType)) {
      selectedFileType = FileType.GZ;
    }

    if (this.excelRegex.test(fileType)) {
      selectedFileType = FileType.EXCEL;
    }

    if (this.videoRegex.test(fileType)) {
      selectedFileType = FileType.VIDEO;
    }

    if (this.audioRegex.test(fileType)) {
      selectedFileType = FileType.AUDIO;
    }

    if (this.docRegex.test(fileType)) {
      selectedFileType = FileType.DOC;
    }

    if (this.textRegex.test(fileType)) {
      selectedFileType = FileType.TEXT;
    }

    if (this.PPTRegex.test(fileType)) {
      selectedFileType = FileType.PPT;
    }

    if (this.SQLRegex.test(fileType)) {
      selectedFileType = FileType.SQL;
    }

    if (this.JSONRegex.test(fileType)) {
      selectedFileType = FileType.JSON;
    }

    if (this.XMLRegex.test(fileType)) {
      selectedFileType = FileType.XML;
    }

    if (this.CLASSRegex.test(fileType)) {
      selectedFileType = FileType.OTHER;
    }

    return selectedFileType;
  }

  openDocumentInNewTab(url: string) {
    const safeUrl = encodeURI(url).replace(/#/g, "%23");
    window.open(safeUrl, "_blank");
  }

  promiseInterval(condition: () => boolean, interval: number = 1000, maxCount: number = 5): Promise<boolean> {
    return new Promise(resolve => {
      let iCount = 0;
      const intervalRef = setInterval(() => {
        if (iCount >= maxCount) {
          resolve(false);
          clearInterval(intervalRef);
          return;
        }
        iCount++;
        if (condition()) {
          resolve(true);
          clearInterval(intervalRef);
        }
      }, interval);
    });
  }

  ascendingOrder(_list: any[] = [], key: string) {
    _list?.sort((a: any, b: any) => a[key] - b[key]);
  }

  descendingOrder(_list: any[] = [], key: string) {
    _list?.sort((a: any, b: any) => b[key] - a[key]);
  }

  //#endregion

  //#region File Upload

  //Regex
  readonly imageRegex = /\.(png|jpg|jpeg|tiff|icon|gif)$/i;
  readonly pdfRegex = /\.(pdf)$/i;
  readonly zipRegex = /\.(zip|tar)$/i;
  readonly gzRegex = /\.(gz)$/i;
  readonly rarRegex = /\.(rar)$/i;
  readonly PPTRegex = /\.(pptx|ppt|odp)$/i;
  readonly excelRegex = /\.(xlsx|xls|csv)$/i;
  readonly videoRegex = /\.(mp4|mov|avi|mkv|flv)$/i;
  readonly audioRegex = /\.(mp3|wav|ogg|flac)$/i;
  readonly docRegex = /\.(doc|docx|DOC|DOCX)$/i;
  readonly textRegex = /\.(txt|TEXT)$/i;
  readonly SQLRegex = /\.(sql)$/i;
  readonly JSONRegex = /\.(json)$/i;
  readonly XMLRegex = /\.(xml)$/i;
  readonly CLASSRegex = /\.(class|css|scss)$/i;

  //#endregion

  //#region Menu Master

  generateRecursion(menuList: any[], mainKey: string = "MenuID", parentKey: string = "ParentMenuID") {
    menuList = menuList.sort((a: any, b: any) => a[mainKey] - b[mainKey]);
    menuList = menuList.map((item: any) => {
      if (!item[parentKey]) item[parentKey] = null;
      return item;
    });
    menuList.map(data => {
      data.ChildList = [];
      data.IsExpanded = false;
      data.IsSelected = false;
      return data;
    });

    const generateNestedStructure = (data: any[], ParentMenuID = null, mainKey: string, parentKey: string) => {
      const result = [];
      for (const item of data) {
        if (item[parentKey] === ParentMenuID) {
          const ChildList = generateNestedStructure(data, item[mainKey], mainKey, parentKey);

          if (ChildList.length > 0) {
            item.ChildList = ChildList;
          }

          result.push(item);
        }
      }
      return result;
    };

    const selectedMenu = generateNestedStructure(menuList, null, mainKey, parentKey);
    return selectedMenu;
  }

  // Generate Tree Depth
  addTreeDepth(menuList: any[], depth = 0) {
    for (const item of menuList) {
      item.treeDepth = depth;
      if (item.ChildList && item.ChildList.length > 0) {
        this.addTreeDepth(item.ChildList, depth + 1);
      }
    }
  }

  orderNestedStructure(data: any[], columnKey: string) {
    data?.sort((a, b) => a[columnKey] - b[columnKey]);
    data.forEach(el => {
      if (el.ChildList?.length) this.orderNestedStructure(el.ChildList, columnKey);
    });
  }

  removeRecursion(menuList: any[], removeChildList: boolean = true) {
    const menuMaster = JSON.parse(JSON.stringify(menuList));
    const selectedMenu = [];
    (function reFormat(ChildList: any[]) {
      for (let index = 0; index < ChildList.length; index++) {
        const element = ChildList[index];
        selectedMenu.push(element);
        if (element.ChildList.length) {
          reFormat(element.ChildList);
        }
      }
    })(menuMaster);

    // Delete ChildList
    for (let i = 0; i < selectedMenu.length; i++) {
      const element = selectedMenu[i];
      if (removeChildList) delete element["ChildList"];
    }
    return selectedMenu;
  }

  //#endregion

  //#region Mat Table

  onDefaultColumn$(tableName: string) {
    const ngUnsubscribe = new Subject<void>();
    let ngUnsubscribe1 = new Subscription();
    return new Observable(obs => {
      ngUnsubscribe1 = this.store.select(select_MOUI_DynamicTableColumn).subscribe((res: any) => {
        const response = JSON.parse(JSON.stringify(res));
        if (!response.IsLoaded) {
          this.store.dispatch(GET_MOBIOFFICE_UI_DynamicTableColumn({ tableName }));
        } else {
          if (!response[tableName]) {
            this.store.dispatch(GET_MOBIOFFICE_UI_DynamicTableColumn({ tableName }));
          } else {
            ngUnsubscribe1.unsubscribe();
            this.setDefaultColumns(obs, response, tableName);
          }
        }
      });
    }).pipe(take(1), takeUntil(ngUnsubscribe));
  }

  setDefaultColumns(obs: any, res: any, tableName: string) {
    if (!res[tableName]) obs.next({});
    const currentColumn = JSON.parse(JSON.stringify(res[tableName]));
    const tableTotalActiveColumns = currentColumn.TableColumns.filter((item: any) => item.IsActive)?.sort((a: any, b: any) => a.Index - b.Index); //Active Columns
    const dataColumnList = tableTotalActiveColumns.filter((item: any) => item.IsDataColumn); // Only column which render on HTML
    currentColumn.TableColumns = dataColumnList?.map((item: any) => {
      const tempModal: any = {
        columnKey: "",
        headingName: "",
        valueKey: "",
        className: "",
        columnType: "",
        columnHeaderAlignment: "",
        columnBodyAlignment: "",
      };
      tempModal.columnKey = item.Key;
      tempModal.headingName = item.Name;
      tempModal.valueKey = item.Key;
      tempModal.className = `${item.Class} ${item.CustomCssClass}`;
      tempModal.columnType = item.Type;
      tempModal.columnHeaderAlignment = item.HeaderAlignment;
      tempModal.columnBodyAlignment = item.BodyAlignment;
      return tempModal;
    });

    //Default Columns
    const defaultTableColumn = currentColumn.defaultTableColumn?.split(",").filter((item: any) => Boolean(item));
    const perDefaultCol = tableTotalActiveColumns.filter((item: any) => !item.IsDataColumn).map((item: any) => item.Name);
    if (defaultTableColumn.length) {
      currentColumn.defaultTableColumn = defaultTableColumn
        .map((item: string) => {
          if (perDefaultCol.find((el: string) => el?.trim() === item?.trim())) return item;
          return currentColumn.TableColumns.find((_el: any) => _el.headingName?.trim() === item?.trim())?.columnKey;
        })
        ?.filter((item: any) => Boolean(item));
    }

    //Total Columns
    currentColumn.TotalColumns = tableTotalActiveColumns?.map((item: any) => {
      const tempModal: any = {
        column: "",
        columnName: "",
      };
      tempModal.column = item.Key;
      tempModal.columnName = item.Name;

      //Check Non Column
      const nonCol = perDefaultCol.find((el: string) => el?.trim() === item.Name?.trim());
      if (nonCol) {
        tempModal.column = nonCol;
        tempModal.columnName = nonCol;
      }
      return tempModal;
    });

    const getFinalColumnByTableName = (tableName: string, existingColumns: string[], totalColumns: any[]) => {
      const columns = this.storageHandlerService.getItem("TableColumns");
      if (!columns) {
        return existingColumns;
      }

      const columnList = JSON.parse(columns);
      const findItem = columnList.find((item: any) => item.TableName === tableName);
      if (!findItem) {
        return existingColumns;
      }

      const foundColumns = findItem.Columns.split(",");
      const actualTotalColumns = totalColumns.map((item: any) => item.column);
      const isActionColumn = actualTotalColumns.find(el => el === "Action");
      const finalColumns = foundColumns.filter((column: string) => actualTotalColumns.includes(column));
      if (isActionColumn) finalColumns.push("Action");
      return finalColumns.filter((item: string, index: number) => finalColumns.indexOf(item) === index);
    };

    currentColumn.defaultTableColumn = getFinalColumnByTableName(tableName, currentColumn.defaultTableColumn, currentColumn.TotalColumns);
    const columnObj = {
      TableColumns: currentColumn.TableColumns,
      TotalColumns: currentColumn.TotalColumns,
      DefaultColumns: currentColumn.defaultTableColumn,
    };
    obs.next(columnObj);
    obs.unsubscribe();
  }

  //#region Theme

  changeTheme(theme: "dark" | "white") {
    const root = document.documentElement;
    root.setAttribute("theme", theme);
    this.anotherSubscription = this.store.select(select_MOUI_ApplicationDetails).subscribe(res => {
      const variables = res.ApplicationDetails.A_SVList;
      const root = document.documentElement;
      if (variables?.length) {
        variables
          .filter((el: any) => el.Theme === theme)
          .forEach((element: any) => {
            root?.style.setProperty(element.Name, element.Value, "important");
          });
      }
    });
  }

  get currentTheme() {
    const root = document.documentElement;
    return root.getAttribute("theme") || "dark";
  }

  //#endregion
}
